import React, { useEffect, useId, useRef, useState } from 'react';
import { Modal } from '../../components/Modal';
import Button from '../../components/ui/Button';

/**
 * "Email to vendor" on a draft vendor return (D44 #14, VR-6). The address starts
 * as the registered vendor's email on file and can be changed; the PDF goes with
 * it. The return becomes Sent only when the email went, so the dialog says what
 * sending does before anyone presses it.
 *
 * Rendered only while open; the page mounts it once it has the email on file.
 */
export const EmailToVendorDialog: React.FC<{
  debitNoteNumber: string;
  /** The registered vendor's email on file; '' when there is none. */
  initialTo: string;
  /** False when the invoice has no registered vendor, so no email is on file. */
  vendorMatched: boolean;
  attachmentName: string;
  goods: boolean;
  busy?: boolean;
  onCancel: () => void;
  onSend: (body: { to: string; message: string }) => void;
}> = ({ debitNoteNumber, initialTo, vendorMatched, attachmentName, goods, busy = false, onCancel, onSend }) => {
  const [to, setTo] = useState(initialTo);
  const [message, setMessage] = useState('');
  const toId = useId();
  const messageId = useId();
  const toRef = useRef<HTMLInputElement>(null);

  // Modal focuses its own panel a tick after opening; the address is what is checked first.
  useEffect(() => {
    const t = window.setTimeout(() => toRef.current?.focus(), 0);
    return () => window.clearTimeout(t);
  }, []);

  const missingTo = to.trim() === '';

  return (
    <Modal isOpen title={`Email ${debitNoteNumber} to the vendor`} onClose={onCancel} maxWidthClass="max-w-md">
      <div className="space-y-3">
        <p className="text-xs text-slate-600">
          Sends the vendor&apos;s copy ({attachmentName}): the invoice, the lines, quantities, unit prices, VAT and
          total. The reason is not on it. Once the email has gone the return is Sent
          {goods ? ' and its goods are held in their batch' : ''}; it can no longer be edited.
        </p>
        {!initialTo && (
          <div
            className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
            data-testid="email-no-address-on-file"
          >
            {vendorMatched
              ? 'The vendor has no email on file. Type the address to send it to.'
              : 'This invoice is not matched to a registered vendor, so there is no email on file. Type the address to send it to.'}
          </div>
        )}
        <div>
          <label htmlFor={toId} className="block text-xs font-medium text-slate-600 mb-1">
            To
          </label>
          <input
            id={toId}
            ref={toRef}
            type="text"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="accounts@vendor.com"
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:border-primary-500"
          />
          <p className="mt-1 text-xs text-slate-500">Several addresses can be separated by commas.</p>
        </div>
        <div>
          <label htmlFor={messageId} className="block text-xs font-medium text-slate-600 mb-1">
            Message (optional, above the standard text)
          </label>
          <textarea
            id={messageId}
            rows={3}
            maxLength={2000}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs focus:border-primary-500 resize-y"
          />
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button onClick={onCancel} disabled={busy}>
            Back
          </Button>
          <Button
            variant="primary"
            onClick={() => onSend({ to: to.trim(), message: message.trim() })}
            loading={busy}
            disabled={missingTo}
          >
            Send email
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EmailToVendorDialog;
